import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu as MenuIcon, X } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";

export const Header = ({ t, lang, setLang }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const links = [
    { to: "/", label: t.nav.home, id: "home" },
    { to: "/menu", label: t.nav.menu, id: "menu" },
    { to: "/breakfast", label: t.nav.breakfast, id: "breakfast" },
    { to: "/lunch", label: t.nav.lunch, id: "lunch" },
    { to: "/sweets", label: t.nav.sweets, id: "sweets" },
    { to: "/catering", label: t.nav.catering, id: "catering" },
    { to: "/story", label: t.nav.story, id: "story" },
    { to: "/contact", label: t.nav.contact, id: "contact" },
  ];

  const goOrder = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: "order" } });
    else scrollToId("order");
  };

  return (
    <header className="sticky top-0 z-[80] bg-navy text-cream shadow-lg" data-testid="site-header">
      <div className="bg-brand py-1.5 text-center" data-testid="announce-bar">
        <p className="font-mono text-[10px] font-medium tracking-[0.25em] text-navy">{t.announce}</p>
      </div>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-8">
        <Link to="/" data-testid="logo-btn" className="flex items-center gap-2.5" onClick={() => setOpen(false)}>
          <img src="/logo.png" alt="the Kurkuma logo" className="h-10 w-auto" data-testid="header-logo" />
          <span className="leading-tight">
            <span className="block font-display text-lg font-bold tracking-wide">KURKUMA</span>
            <span className="block text-[10px] font-medium tracking-[0.35em] text-brand">AI KITCHEN</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-6 xl:flex" aria-label="Main">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              className={`text-[13px] font-medium tracking-wide transition-colors hover:text-brand ${
                location.pathname === l.to ? "text-brand" : "text-cream/80"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex items-center overflow-hidden rounded-full border border-white/20" role="group" aria-label="Language">
            {["en", "pl"].map((l) => (
              <button
                key={l}
                data-testid={`lang-toggle-${l}`}
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                className={`px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                  lang === l ? "bg-brand text-navy" : "text-cream/70 hover:text-cream"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <button
            data-testid="header-order-btn"
            onClick={goOrder}
            className="hidden rounded-full bg-brand px-5 py-2 text-[13px] font-semibold text-navy transition-all hover:bg-branddark hover:shadow-lg sm:block"
          >
            {t.orderNow}
          </button>
          <button
            className="text-cream xl:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-btn"
          >
            {open ? <X className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-white/10 bg-navy px-4 pb-6 pt-2 xl:hidden" aria-label="Mobile" data-testid="mobile-nav">
          {links.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              onClick={() => setOpen(false)}
              className={`block border-b border-white/5 py-3 text-sm font-medium ${
                location.pathname === l.to ? "text-brand" : "text-cream/80"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <button
            data-testid="mobile-order-btn"
            onClick={goOrder}
            className="mt-4 w-full rounded-full bg-brand px-5 py-3 text-sm font-semibold text-navy"
          >
            {t.orderNow}
          </button>
        </nav>
      )}
    </header>
  );
};
